import { Injectable, inject } from '@angular/core';
import { RegisterUserFormTechModePayload, RegisterUserFormValues } from '../interfaces';
import { VisitorRegistrationBeforeModularFormField, SendRegisterUserResponse } from '../interfaces/register-user.response';
import { CUSTOM_ROUTE_MOBILE_MAP } from '../../custom-route/components/custom-route-mobile/config/constants';
import { CUSTOM_ROUTE_DESKTOP_MAP } from '../../custom-route/components/custom-route-desktop/config/constants';
import { RegisterUserService } from './register-user.service';
import { CustomRouteService } from '../../custom-route/services/custom-route.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { RouteDataService } from '../../custom-route/shared/map/services/route-data.service';
import { EditRouteService } from '../../custom-route/shared/planner/components/route-timeline/components/daily-route-list/services';
import { HttpErrorResponse } from '@angular/common/http';
import { customRouteAdapter } from '../../custom-route/adapters/custom-route.adapter';
import { DailyRouteResponse } from '../../custom-route/interfaces/custom-route.response';
import { openCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { UpdateRecommendedRouteTechMode } from '../../custom-route/shared/save-recommeded-route-tech-mode-view/services';
import { UpdateRecommededRouteTechModePayload } from '../../custom-route/shared/save-recommeded-route-tech-mode-view/interfaces';
import { RegisterUserFormTechMode } from './register-user-form-tech-mode';
import { RecommededRouteListPlanner2 } from '../../home/services';
import { RecommendedRouteNewTokenPayload } from '../../home/interfaces';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { EmbedContextService } from 'src/app/services/embed-context.service';
import { SEARCH_RADIUS_NEEDED_ACTION_VALUE } from '../shared/components/search-radius-field/config/constants';
import { TranslationService } from 'src/app/services';

@Injectable({
  providedIn: 'root'
})
export class RegisterUserSubmissionService {
  // Service injections
  private _translationService = inject(TranslationService);
  private _sessionStorageService = inject(SessionStorageService);
  private _registerUserService = inject(RegisterUserService);
  private _customRouteService = inject(CustomRouteService);
  private _editRouteService = inject(EditRouteService);
  private _routeDataService = inject(RouteDataService);
  private _router = inject(Router);
  private _registerUserFormTechMode = inject(RegisterUserFormTechMode);
  private _updateRecommendedRouteTechModeService = inject(UpdateRecommendedRouteTechMode);
  private _recommededRouteListPlanner2Service = inject(RecommededRouteListPlanner2);
  private _dataChatService = inject(DataChatService);
  private _embedContext = inject(EmbedContextService);

  // Service state signals
  private _selectedLanguage = this._translationService.selectedLanguage;

  /**
   * Initializes the component by resetting services and setting initial states.
   */
  public initializeComponent(): void {
    // Reset services
    this._registerUserService.getDataRegisterUserServiceSignal();
    this._customRouteService.reset();
    this._editRouteService.resetEditState();
    this._routeDataService.resetRouteSegments();
    this._routeDataService.resetCurrentStepAndTrame();

    // Set initial states
    this._sessionStorageService.setItem('customRouteMobileViewsState', CUSTOM_ROUTE_MOBILE_MAP.planner);
    this._sessionStorageService.setItem('customRouteDesktopViewsState', CUSTOM_ROUTE_DESKTOP_MAP.map);
    this._sessionStorageService.removeItem('registerUserForm');
  }
  
  public handleRegisterUserFormSubmission(
    formValues: RegisterUserFormValues, 
    modularFieldDefinitions: VisitorRegistrationBeforeModularFormField[] = [],
    recommendedRouteToken: string | null = null
  ): void {
    // if no recommended route token, user is creating a new recommended route
    if (!recommendedRouteToken) {
      this._registerUserService.setRegisterUserFormData(formValues);
      
      const request$ = this._registerUserService.sendRegisterUserData(formValues, modularFieldDefinitions);
      this._processSubmission(request$);
      
    } else {
      // if recommended route token, user is updating an existing recommended route
      const payload: RecommendedRouteNewTokenPayload = {
        token: recommendedRouteToken,
        start_location: {
          address: formValues.startLocationAddress,
          long: formValues.startLocationLng,
          lat: formValues.startLocationLat,
        },
        start_date: formValues.startDate,
        end_date: formValues.endDate,
        start_time: formValues.startTime,
      };
      this._recommededRouteListPlanner2Service.getRecommededRoutePlanner2NewToken(payload).subscribe({
        next: (newToken: { token: string }) => {
          this._router.navigateByUrl(`/custom-route/v3/${newToken.token}`);
        },
        error: (err) => {
          console.error('Error getting recommended route new token', err);
        }
      });
    }
  }

  public handleRegisterUserTechModeFormSubmission(
    formValues: RegisterUserFormTechModePayload, 
    recommendedRouteToken: string | null = null
  ): void {
    if (!recommendedRouteToken) {
      // create new recommended route
      this._registerUserFormTechMode.sendRegisterUserTechModeForRecommendedRouteServiceSignal(formValues);
      
    } else {
      // update existing recommended route
      const payload: UpdateRecommededRouteTechModePayload = {
        token: recommendedRouteToken,
        start_location: formValues.start_location,
        start_date: formValues.start_date,
        end_date: formValues.end_date,
        start_time: formValues.start_time,
        is_active: true,
        // HRS-2826: la privacidad también es editable
        private: formValues.private,
      };
      this._updateRecommendedRouteTechModeService.updateRecommendedRouteTechModeFromRegisterUserServiceSignal(payload);
    }
  }

  private _processSubmission(request$: Observable<SendRegisterUserResponse>): void {
    // Embed only: cue the "generating your route" spinner while the first route
    // is in flight (the planner isn't mounted yet, so it can't show its own).
    if (this._isChatbotEmbed()) this._dataChatService.generatingRoute.set(true);

    request$.subscribe({
      next: (registerResponse: SendRegisterUserResponse) => {
        // avoid navigating to the custom route if the search radius is needed to be increased
        if (registerResponse.needs_action === SEARCH_RADIUS_NEEDED_ACTION_VALUE) {
          this._registerUserService.setExpandSearchRadiusMessage(registerResponse.message[this._selectedLanguage()]);
          this._registerUserService.setIsLoadingFirstCustomRoute(false);
          return;
        }
        
        this._customRouteService.setIsWelcomeViewShown(true);
        this._customRouteService.setToken(registerResponse.token);

        this._customRouteService.setRegisterUserBeforeFormValues(registerResponse.registerUserBeforeFormValues ?? null);

        const adaptedRouteData = customRouteAdapter(registerResponse.id, registerResponse.dailyRoutes as DailyRouteResponse[]);
        this._customRouteService.setOriginalCustomRouteData(adaptedRouteData);

        // Embedded chatbot widget: don't navigate away. We only set the token above;
        // the inline <app-custom-route> mounts off that token and loads the canonical
        // route via get-route-completed — exactly like the site does after navigation.
        // This is what makes editing/sync-route work, because the loaded route carries
        // the real `id` the sync body needs (feeding the get-route-planning response
        // straight into editedCustomRouteData skipped that and broke edits).
        if (this._isChatbotEmbed()) {
          this._dataChatService.generatingRoute.set(false);
          return;
        }

        this._navigateToCustomRoute(registerResponse.token);
      },
      error: (error: HttpErrorResponse) => {
        console.error('Submission error:', error);
        this._dataChatService.generatingRoute.set(false);
        this._openErrorSendRegisterUserSnackbar();
      }
    });
  }

  /** True when running inside the embeddable chatbot widget route. */
  private _isChatbotEmbed(): boolean {
    return this._embedContext.isEmbed();
  }

  /**
   * Navigates to the custom route page.
   */
  private _navigateToCustomRoute(token: string): void {
    const targetUrl = `/custom-route/v3/${token}`;
    
    this._router.navigateByUrl(targetUrl);
  }

  private _openErrorSendRegisterUserSnackbar(): void {
    openCiSnackbar({
      content: this._translationService.instant('Error al enviar la información. Inténtalo de nuevo'),
      status: 'error',
      'icon-name': 'error',
      'horizontal-position': 'end',
      'vertical-position': 'bottom',
      duration: 4000
    });
  }
}
